import useFetcher from "@/utils/use-fetcher";
import {
  getGetOrderSheetTabKey,
  GetOrderSheetTabRequest,
  GetOrderSheetTabResponse,
} from "@/features/line-sheet-sets/hooks/use-get-order-sheet-tab";
import {
  getGetLineSheetTabKey,
  GetLineSheetTabRequest,
  GetLineSheetTabResponse,
} from "@/features/line-sheet-sets/hooks/use-get-line-sheet-tab";
import { useMemo } from "react";
import { AxiosRequestConfig } from "axios";
import {
  ListAllSheetTabsRequest,
  ListAllSheetTabsResponse,
  parseListAllSheetTabsKey,
} from "@/features/line-sheet-sets/hooks/use-list-all-sheet-tabs";
import { SheetProduct } from "@/features/order-sheets/sheet.type";
import { createOrderSheetProductFinder } from "@/features/line-sheet-sets/helpers/order-sheet-product-finder";

export default function useListAllSheetTabsFetcher() {
  const getOrderSheetTabFetcher = useFetcher<
    GetOrderSheetTabRequest,
    GetOrderSheetTabResponse
  >();
  const getLineSheetTabFetcher = useFetcher<
    GetLineSheetTabRequest,
    GetLineSheetTabResponse
  >();

  return useMemo(() => {
    return async (
      config: AxiosRequestConfig<ListAllSheetTabsRequest>
    ): Promise<ListAllSheetTabsResponse> => {
      const { parameter, request } = parseListAllSheetTabsKey(config);

      const responses = await Promise.all(
        request.sheetTabIndexes.flatMap((tabIndex) => {
          return [
            getOrderSheetTabFetcher(
              getGetOrderSheetTabKey({
                by: parameter.by,
                companyId: parameter.companyId,
                orderSheetSetId: parameter.orderSheetSetId,
                orderSheetId: parameter.orderSheetId,
                orderSheetTabIndex: tabIndex,
              })
            ),
            getLineSheetTabFetcher(
              getGetLineSheetTabKey({
                by: parameter.by,
                companyId: parameter.companyId,
                lineSheetSetId: request.lineSheetSetId,
                lineSheetId: request.lineSheetId,
                lineSheetTabIndex: tabIndex,
              })!!
            ),
          ];
        })
      );

      const sheetTabs = [];

      for (let i = 0; i < responses.length; i += 2) {
        const orderSheetTab = responses[i] as GetOrderSheetTabResponse;
        const lineSheetTab = responses[i + 1] as GetLineSheetTabResponse;

        // 두 목록은 같은 순번끼리가 아니라 상품 id 로 짝짓는다 — 상품 수가 다르면(재고가 전부 0 인
        // 상품은 바이어의 라인시트 목록에서만 빠진다) 순번이 밀려 남의 공급가·주문 수량이 붙는다.
        const findOrderSheetProduct = createOrderSheetProductFinder(
          orderSheetTab.products
        );
        const sheetProducts = lineSheetTab.products.map(
          (lineSheetProduct, index): SheetProduct => {
            const orderSheetProduct = findOrderSheetProduct(
              lineSheetProduct,
              index
            );
            if (orderSheetProduct) {
              return {
                ...lineSheetProduct,
                supplyPrice: orderSheetProduct.supplyPrice,
                priceAdjustedBy: orderSheetProduct.priceAdjustedBy,
                orderQuantityWithOptionList:
                  orderSheetProduct.orderQuantityWithOptionList,
                confirmedPriceAdjustedBy:
                  orderSheetProduct.confirmedPriceAdjustedBy,
                confirmedPrice: orderSheetProduct.confirmedPrice,
                confirmedPriceWithoutCondition: orderSheetProduct.confirmedPriceWithoutCondition,
                newStockWithOptionList:
                  orderSheetProduct.newStockWithOptionList,
                confirmedQuantityWithOptionList:
                  orderSheetProduct.confirmedQuantityWithOptionList,
                firstOrderQuantityWithOptionList:
                orderSheetProduct.firstOrderQuantityWithOptionList,
                prevOrderQuantityWithOptionList:
                orderSheetProduct.prevOrderQuantityWithOptionList,
                latestOrderQuantityWithOptionList:
                  orderSheetProduct.latestOrderQuantityWithOptionList,
              };
            } else {
              return {
                ...lineSheetProduct,
                orderQuantityWithOptionList: [],
                confirmedPriceAdjustedBy: null,
                confirmedPrice: null,
                confirmedPriceWithoutCondition: null,
                newStockWithOptionList: null,
                confirmedQuantityWithOptionList: null,
                firstOrderQuantityWithOptionList: null,
                prevOrderQuantityWithOptionList: null,
                latestOrderQuantityWithOptionList: null,
              };
            }
          }
        );

        sheetTabs.push({
          index: lineSheetTab.index,
          name: lineSheetTab.name,
          products: sheetProducts,
        });
      }

      return Promise.resolve({
        _orderSheetSetId: parameter.orderSheetSetId,
        _orderSheetId: parameter.orderSheetId,
        sheetTabs,
      });
    };
  }, [getOrderSheetTabFetcher, getLineSheetTabFetcher]);
}
